import SysRecord from 'app/class/record';
import { useFormContext } from 'react-hook-form';
import useRecordProducerActions from 'modules/record/record-producer/hooks/useRecordProducerActions';
import useRecordProducerActionCompiler from 'modules/record/record-producer/hooks/useRecordProducerActionCompiler';
import { RecordProducerRelatedActionsComponent } from './RecordProducerRelatedActions';
import { useSession } from 'app/hooks/useSession';
import { ReactNode, useState } from 'react';
import FormActionConfirmationDialog, { requiresFormActionConfirmation } from 'modules/record/shared/components/FormActionConfirmationDialog';
import ResponsiveActionBar from 'modules/record/shared/components/ResponsiveActionBar';
import useActionHotkeys from 'modules/record/shared/hooks/useActionHotkeys';
import { allowsRecordAction, RecordPermissions } from 'modules/record/shared/services/actionPermissions';

interface RecordProducerActionsComponentProps {
    tableName: string;
    id?: string;
    leadingContent?: ReactNode;
    permissions?: RecordPermissions;
}

export const RecordProducerActionsComponent = ({ tableName, id, leadingContent, permissions }: RecordProducerActionsComponentProps) => {
    const { getValues } = useFormContext();
    const { data: userSession } = useSession();
    const { actions } = useRecordProducerActions(tableName, {
        form: getValues(),
        currentTableName: tableName,
        isNewRecord: !id,
        user: { id: userSession?.id || "", roles: userSession?.roles || [] }
    });
    const permittedActions = actions.filter(action => allowsRecordAction(action, permissions));
    const { runActionScript } = useRecordProducerActionCompiler();
    const [pendingAction, setPendingAction] = useState<SysRecord | null>(null);
    const [runningActionId, setRunningActionId] = useState<string | null>(null);

    const executeAction = async (action: SysRecord) => {
        setRunningActionId(action.id);

        try {
            await runActionScript(action, tableName);
        } finally {
            setRunningActionId(null);
        }
    };

    const handleActionClick = (action: SysRecord) => {
        if (runningActionId) return;

        if (requiresFormActionConfirmation(action)) {
            setPendingAction(action);
            return;
        }

        executeAction(action);
    };

    const handleConfirmAction = async () => {
        const action = pendingAction;
        setPendingAction(null);

        if (action) {
            await executeAction(action);
        }
    };

    useActionHotkeys({
        actions: permittedActions,
        runningActionId,
        onActionTrigger: handleActionClick,
    });

    if (!permittedActions.length) return null;

    return (
        <>
            <ResponsiveActionBar
                actions={permittedActions}
                runningActionId={runningActionId}
                mobileVisibleCount={1}
                variant="form"
                leadingContent={leadingContent}
                onActionClick={handleActionClick}
                trailingContent={<RecordProducerRelatedActionsComponent tableName={tableName} id={id} />}
            />
            <FormActionConfirmationDialog
                action={pendingAction}
                record={getValues()}
                open={Boolean(pendingAction)}
                onCancel={() => setPendingAction(null)}
                onConfirm={handleConfirmAction}
            />
        </>
    )
}
